/**
 * SessionItem renders a session block inside the weekly calendar grid.
 *
 * Comparison with SessionRow (src/pages/psychologist/tabs/sessions/SessionRow.tsx):
 *   - SessionItem:  operates on SessionWithPsychologist; absolutely positioned
 *     within a time-grid cell, height proportional to session duration. Displays
 *     patient name, country flag (derived from timezone/nationality), start time,
 *     modality icon, and an unblock button for blocked slots. No action buttons.
 *   - SessionRow:   operates on SessionLite; renders as a flex card row in a
 *     list. Shows initials avatar, therapy type, status chips ("Próxima sesión",
 *     "Primera vez"), and action buttons ("Ver proceso", "Ingresar" for Meet).
 *
 * The components share the concept of displaying a session but have different
 * data types, positioning models, and interaction patterns — less than 40%
 * overlap. No shared base component was extracted.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { ROW_HEIGHT } from './constants';
import { gridDeadSegments } from './bookingUtils';
import CountryFlag from '../../common/CountryFlag';
import { timezoneToCountryCode } from '../../../utils/timezoneToCountry';
import { isoToZoneParts, localTzToUTC } from './timeZoneHelpers';
import type { Patient, SessionWithPsychologist } from './data';
import { getPaymentCountdown, type PaymentCountdownInfo } from '../../../utils/paymentCountdown';
import { BA_TIMEZONE, parseBackendSessionDate } from '../../../utils/timezone';
import { useWidth } from '../../../hooks/useWidth';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import { usePsychologistContext } from '../../../contexts/PsychologistContext';
import {
  FormacionBlockDialog,
  FormacionBlockHover,
  isFormacionReason,
  type FormacionBlockData,
} from './FormacionBlockInfo';

// Inyectar keyframe para pulse animation (una sola vez)
if (typeof document !== 'undefined' && !document.getElementById('psi-reschedule-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'psi-reschedule-pulse-style';
  style.textContent = `@keyframes psi-reschedule-pulse{0%,100%{opacity:0.85;background:rgba(109,40,217,0.15);border-color:rgba(109,40,217,0.5);box-shadow:0 0 0 0 rgba(109,40,217,0.3)}50%{opacity:0.3;background:rgba(109,40,217,0.65);border-color:rgba(109,40,217,1);box-shadow:0 0 14px 6px rgba(109,40,217,0.3)}}`;
  document.head.appendChild(style);
}

// Helper function to convert nationality to ISO2 country code (fallback)
const nationalityToISO2 = (nationality?: string): string | null => {
  if (!nationality) return null;
  const mapping: Record<string, string> = {
    'AR': 'AR', 'Argentina': 'AR',
    'US': 'US', 'United States': 'US', 'USA': 'US',
    'MX': 'MX', 'Mexico': 'MX',
    'ES': 'ES', 'Spain': 'ES', 'España': 'ES',
    'CO': 'CO', 'Colombia': 'CO',
    'CL': 'CL', 'Chile': 'CL',
    'PE': 'PE', 'Peru': 'PE', 'Perú': 'PE',
    'BR': 'BR', 'Brazil': 'BR', 'Brasil': 'BR',
    'UY': 'UY', 'Uruguay': 'UY',
    'PY': 'PY', 'Paraguay': 'PY',
    'BO': 'BO', 'Bolivia': 'BO',
    'EC': 'EC', 'Ecuador': 'EC',
    'VE': 'VE', 'Venezuela': 'VE',
  };
  const upper = nationality.toUpperCase().trim();
  return mapping[upper] || mapping[nationality] || null;
};

// Helper function to get country code from patient (timezone first, nationality as fallback)
const getPatientCountryCode = (patient?: Patient | null): string | null => {
  if (!patient) return null;
  
  // Try timezone first (more accurate)
  const timezoneCode = timezoneToCountryCode(patient.timezone);
  if (timezoneCode) return timezoneCode;
  
  // Fallback to nationality if timezone is not available or cannot be mapped
  return nationalityToISO2(patient.nationality);
};

function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

type Props = {
  session: SessionWithPsychologist;
  psychologistId: string;
  rowStartMin: number;
  onClick?: () => void;
  onUnblock?: (psychologistId: string, fromDate: Date, toDate: Date, sesionId: string, blockageIds?: string[]) => void;
  patients?: Patient[];
  isPast?: boolean;
  isBeingRescheduled?: boolean;
  /** Cuando dos o más sesiones activas se solapan en el mismo slot, cada una recibe su
   * índice de columna y el total de columnas para repartirse el ancho de la celda en
   * vez de ocupar el 100% y taparse entre sí. Omitir cuando la sesión ocupa la celda sola. */
  columnIndex?: number;
  columnCount?: number;
  /** El bloque se dibuja pero no recibe mouse (ni hover ni click): los eventos pasan a la celda de abajo.
   * Lo usa la grilla en modo agendar para que un bloqueo no impida elegir el horario ni tape el preview
   * — la psicóloga puede agendar encima y el bloqueo se ajusta alrededor de la sesión. */
  inert?: boolean;
};

const SessionItem: React.FC<Props> = ({ session, rowStartMin, onClick, onUnblock, psychologistId, patients = [], isPast = false, isBeingRescheduled = false, columnIndex, columnCount, inert = false }) => {
  const { size } = useWidth();
  const isMobile = size === 's';
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  // Bandera de la fila 2: siempre el huso horario VIGENTE de la psicóloga (no el que tenía
  // al momento de agendar la sesión) — se recalcula en cada render a partir de therapistTz.
  const therapistCountryCode = timezoneToCountryCode(therapistTz) || 'AR';
  // Find patient by email to get timezone/nationality
  const patientData = patients.find(p => p.email === session.patient.email);

  // Payment countdown — only for unpaid, future sessions
  const isPendingPayment = !isPast &&
    session.status !== 'active' &&
    session.status !== 'free' &&
    session.status !== 'settled' &&
    session.status !== 'blocked';

  const [countdown, setCountdown] = useState<PaymentCountdownInfo | null>(() => {
    if (!isPendingPayment) return null;
    const start = parseBackendSessionDate(session.origISO || `${session.date} ${session.time}:00`, BA_TIMEZONE);
    return getPaymentCountdown(start, therapistTz);
  });

  useEffect(() => {
    if (!isPendingPayment) { setCountdown(null); return; }
    const update = () => {
      const start = parseBackendSessionDate(session.origISO || `${session.date} ${session.time}:00`, BA_TIMEZONE);
      setCountdown(getPaymentCountdown(start, therapistTz));
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [session.date, session.time, session.origISO, isPendingPayment, therapistTz]);
  // Derive country code from the full patient record (patients list) first,
  // then fall back to the timezone embedded in the session itself.
  const countryCode = getPatientCountryCode(patientData || null)
    || (session.patient.timezone ? timezoneToCountryCode(session.patient.timezone) : null);

  // Hora local del paciente en su propio huso horario. Se calcula siempre (incluso cuando
  // coincide numéricamente con la hora de la psicóloga) porque lo que determina si se
  // muestra la Fila 3 es si el país del paciente difiere del de la psicóloga, no si el
  // horario difiere — dos husos con el mismo offset (ej. Ecuador y Perú, ambos UTC-5)
  // deben igual mostrar ambas banderas.
  const patientLocalTime = useMemo(() => {
    const tz = patientData?.timezone || session.patient.timezone;
    if (!tz) return null;
    try {
      const utcStart = localTzToUTC(session.date, session.time, therapistTz);
      return isoToZoneParts(utcStart.toISOString(), tz).time;
    } catch {
      return null;
    }
  }, [session.date, session.time, patientData?.timezone, session.patient.timezone, therapistTz]);

  // Si el país del paciente difiere del de la psicóloga pero no pudimos calcular su hora
  // local (sin timezone válido, ej. solo nationality), mostramos igual la fila 3 usando la
  // hora de la sesión como fallback — el dato relevante ahí es la bandera, no el horario.
  const isDifferentCountry = !!countryCode && countryCode !== therapistCountryCode;
  const patientDisplayTime = patientLocalTime ?? (isDifferentCountry ? session.time : null);

  const remToPx = (rem: number): number => {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return rem * fontSize;
  }
  const ROW_HEIGHT_PX = remToPx(ROW_HEIGHT);
  
  const sStart = timeToMinutes(session.time);
  const sessionDurationMinutes = session.duration || 60;
  const sEnd = sStart + sessionDurationMinutes;

  // Si la sesión empezó antes de esta celda (porque scrolleamos y la celda de inicio salió de vista),
  // recortamos: offset=0 y la altura solo cubre la parte visible restante
  const clippedStart = Math.max(sStart, rowStartMin);
  const offsetMinsInRow = clippedStart - rowStartMin;
  const offsetPx = Math.round((offsetMinsInRow / 60) * ROW_HEIGHT_PX);
  const visibleDuration = sEnd - clippedStart;
  const heightPx = Math.max(20, (visibleDuration / 60) * ROW_HEIGHT_PX - 2);
  // Con el layout normal de 3 filas, un bloque de 30 min (~26px) no alcanza a mostrar ni
  // el nombre completo ni la fila de hora sin recortarse — por debajo de este alto se
  // colapsa todo a una sola línea "Nombre · hora" en vez de dejar contenido cortado.
  const isCompact = heightPx < 40;
  const blocked = session.status === 'blocked';
  // Bloqueo creado al anotarse a una formación de Comunidad: muestra título, hora y link de
  // Meet (tooltip al pasar el mouse + cartel al hacer click).
  const [formacionDetailsOpen, setFormacionDetailsOpen] = useState(false);
  const formacionData = useMemo<FormacionBlockData | null>(() => {
    if (!blocked || !isFormacionReason(session.patient.name)) return null;
    const start = session.origISO ? new Date(session.origISO) : new Date(`${session.date}T${session.time}`);
    if (Number.isNaN(start.getTime())) return null;
    return {
      reason: session.patient.name,
      startIso: start.toISOString(),
      endIso: new Date(start.getTime() + (session.duration || 60) * 60_000).toISOString(),
    };
  }, [blocked, session.patient.name, session.origISO, session.date, session.time, session.duration]);
  // Las psicólogas impersonadas (CX) pueden eliminar bloqueos aunque sean del mismo día.
  const impersonated = isImpersonated();
  // Porción del bloqueo ya transcurrida (0–100%): se oscurece solo esa franja.
  // Ej: bloqueo 12–18 y son las 14 → el tramo 12–14 se ve oscuro y 14–18 claro.
  const darkPct = useMemo(() => {
    if (!blocked) return 0;
    const start = new Date(`${session.date}T${session.time}`);
    const end = new Date(start.getTime() + (session.duration || 60) * 60 * 1000);
    const nowMs = Date.now();
    if (nowMs >= end.getTime()) return 100;        // ya terminó: todo oscuro
    if (nowMs <= start.getTime()) return 0;         // todavía no empezó: todo claro
    // En curso: oscurecer desde el inicio visible hasta la hora actual
    const now = new Date(nowMs);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const darkMins = Math.min(Math.max(nowMinutes - clippedStart, 0), visibleDuration);
    return (darkMins / visibleDuration) * 100;
  }, [blocked, session.date, session.time, session.duration, clippedStart, visibleDuration]);

  // Tramos que el redondeo de la grilla consume pero la sesión no usa: con un inicio
  // corrido (18:05) quedan bloqueados y vacíos 18:00–18:05 y 19:05–19:30. Sin pintarlos,
  // la psicóloga pierde media hora de agenda sin verlo en ninguna parte.
  const deadSegments = useMemo(
    () => (blocked ? [] : gridDeadSegments(sStart, sessionDurationMinutes, rowStartMin)),
    [blocked, sStart, sessionDurationMinutes, rowStartMin],
  );

  // Patrón rayado base del bloqueo (claro).
  // Rayado de los tramos muertos: mismo lenguaje visual que un bloqueo (ese tiempo está
  // tomado), pero en violeta tenue para leerse como parte de la sesión y no como otra cosa.
  const deadTimeStripes = 'repeating-linear-gradient(-45deg, rgba(139, 92, 246, 0.28), rgba(139, 92, 246, 0.28) 4px, rgba(139, 92, 246, 0.10) 4px, rgba(139, 92, 246, 0.10) 8px)';
  const blockedStripes = 'repeating-linear-gradient(-45deg, rgba(212, 208, 224, 1), rgba(212, 208, 224, 1) 5px, #e5e2ef 5px, #e5e2ef 10px)';
  // Cuando hay sesiones solapadas en el mismo slot, cada una ocupa una columna (ancho
  // repartido en porcentaje) en vez de todo el ancho de la celda.
  const hasColumns = columnCount !== undefined && columnCount > 1 && columnIndex !== undefined;
  const columnWidthPct = hasColumns && columnCount ? 100 / columnCount : null;
  // Posición horizontal, compartida por el bloque de la sesión y por los tramos muertos:
  // si se reparten columnas, los dos tienen que ocupar exactamente la misma.
  const columnStyle: { left: string | number; width?: string; right?: number } =
    hasColumns && columnWidthPct !== null
      ? { left: `${(columnIndex ?? 0) * columnWidthPct}%`, width: `calc(${columnWidthPct}% - 2px)` }
      : { left: 0, right: 0 };
  // Estilos sólidos - ocupan 2px menos de altura que la celda
  const sessionStyles = {
    position: 'absolute' as const,
    top: offsetPx + 1,
    ...columnStyle,
    height: heightPx,
    zIndex: 35, // Por encima de overlays y rangos (WorkRange ~18, ScheduleAction ~30)
    pointerEvents: inert ? 'none' as const : 'auto' as const,
    background: blocked
      ? (darkPct > 0
        ? `linear-gradient(to bottom, rgba(0,0,0,0.24) 0%, rgba(0,0,0,0.24) ${darkPct}%, transparent ${darkPct}%, transparent 100%), ${blockedStripes}`
        : blockedStripes)
      : isPast
      ? '#D4D0E0'
      : '#8b5cf6',
    border: 'none',
    borderRadius: 2,
    padding: isCompact ? '1px 6px' : (isMobile ? '3px 4px' : '4px 8px'),
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: isCompact ? 'center' as const : 'flex-start' as const,
    gap: isCompact ? 0 : (blocked ? 5 : 3),
    color: isPast || blocked ? '#292b30' : '#ffffff',
    fontSize: '0.75rem',
    fontWeight: 700,
    boxShadow: 'none',
    transition: 'opacity 0.2s ease-out',
    cursor: formacionData ? 'pointer' : blocked || !onClick ? 'default' : 'pointer',
    overflow: 'hidden',
    ...(isBeingRescheduled ? {
      background: 'rgba(109, 40, 217, 0.15)',
      border: '2px dashed rgba(76, 29, 149, 0.85)',
      color: '#5b21b6',
      animation: 'psi-reschedule-pulse 1.3s ease-in-out infinite',
    } : {}),
  };
  
  const withFormacionHover = (node: React.ReactElement) =>
    formacionData ? <FormacionBlockHover data={formacionData}>{node}</FormacionBlockHover> : node;

  return (
    <>
      {deadSegments.map(seg => (
        <div
          key={seg.key}
          data-testid={`session-grid-dead-${seg.key}`}
          aria-hidden="true"
          title="Tiempo que queda ocupado por el redondeo de la agenda"
          style={{
            position: 'absolute',
            top: Math.round(((seg.fromMin - rowStartMin) / 60) * ROW_HEIGHT_PX) + 1,
            height: Math.max(2, ((seg.toMin - seg.fromMin) / 60) * ROW_HEIGHT_PX - 1),
            ...columnStyle,
            zIndex: 34,
            pointerEvents: 'none',
            background: deadTimeStripes,
            opacity: isPast ? 0.5 : 1,
          }}
        />
      ))}
    {withFormacionHover(
    <div
      onClick={(e) => {
        // Evita que el click burbujee hasta el onClick de la celda contenedora, que
        // resuelve la sesión "principal" del slot — con sesiones solapadas eso siempre
        // abriría la primera, ignorando cuál de los bloques se clickeó realmente.
        e.stopPropagation();
        if (formacionData) setFormacionDetailsOpen(true);
        else onClick?.();
      }}
      style={sessionStyles}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.9';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      {blocked && !inert && (!isPast || impersonated) && (
        <div
          onMouseEnter={(e) => {
            e.currentTarget.style.scale = '1.2';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.scale = '1';
          }}
          onClick={(e) => {
            e.stopPropagation();
            const start = new Date(`${session.date}T${session.time}`);
            const end = new Date(start.getTime() + (session.duration || 60) * 60 * 1000);
            onUnblock?.(psychologistId, start, end, session.id, session.blockageIds);
          }}
          style={{
            position: 'absolute',
            top: isCompact ? Math.max(0, (heightPx - 18) / 2) : 4,
            right: 8,
            width: 18,
            height: 18,
            borderRadius: '50%',
            transition: 'scale 0.2s ease-in-out',
            color: '#ef4444',
            fontSize: '1rem',
            fontWeight: 900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 36
          }}
          title="Desocupar horario"
        >
          ✕
        </div>
      )}
      {isCompact ? (
        /* Bloque compacto (< 40px, ej. 30 min): nombre + hora en una sola línea — el
           layout de 3 filas normal no entra sin recortarse. */
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          overflow: 'hidden',
          minWidth: 0,
        }}>
          <span title={session.patient.name} style={{
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: isMobile ? '0.65rem' : '0.7rem',
            lineHeight: 1.1,
            minWidth: 0,
          }}>
            {session.patient.name}
          </span>
          {!blocked && (
            <span data-testid="session-item-psych-time" style={{
              flexShrink: 0,
              opacity: 0.9,
              fontSize: '0.6rem',
              fontWeight: 500,
              lineHeight: 1.1,
            }}>
              {session.time}
            </span>
          )}
        </div>
      ) : (
        <>
          {/* Fila 1: nombre del paciente (sin bandera) */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden'
          }}>
            <span title={session.patient.name} style={{
              fontWeight: 700,
              flex: 1,
              overflow: 'hidden',
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              lineHeight: 1.2,
              // Bloque de formación: el título es largo (nombre de la formación + edición), así que
              // se ajusta a varias líneas — en vez de cortarse con "…" — y deja lugar a la ✕.
              ...(formacionData
                ? {
                    whiteSpace: 'normal' as const,
                    overflowWrap: 'anywhere' as const,
                    paddingRight: 22,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical' as const,
                    WebkitLineClamp: Math.max(1, Math.floor((heightPx - 38) / 15)),
                  }
                : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
            }}>
              {session.patient.name}
            </span>
          </div>

          {/* Fila 2: hora del psicólogo + bandera de su huso horario vigente + badge de pago pendiente */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            opacity: 0.9,
            fontSize: '0.7rem',
            fontWeight: 500,
            overflow: 'hidden',
            minWidth: 0
          }}>
            <span data-testid="session-item-psych-time" style={{ flexShrink: 0 }}>{session.time}</span>
            <span data-testid="session-item-psych-flag" aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
              <CountryFlag iso2={therapistCountryCode} size={isMobile ? '0.7rem' : '0.75rem'} />
            </span>
            {countdown && !countdown.isExpired && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1px 4px',
                  borderRadius: 6,
                  background: countdown.isExpired
                    ? 'rgba(239,68,68,0.15)'
                    : countdown.isUrgent
                    ? 'rgba(249,115,22,0.15)'
                    : '#FAF7EE',
                  color: countdown.isExpired
                    ? '#dc2626'
                    : countdown.isUrgent
                    ? '#ea580c'
                    : '#B4700B',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0
                }}
              >
                Pend. pago
              </span>
            )}
          </div>

          {/* Fila 3: hora local del paciente + su bandera (desktop, sólo si su país es distinto al
              de la psicóloga — no alcanza con que la hora numérica coincida, ej. Ecuador/Perú
              comparten offset UTC-5 pero son países distintos y deben verse ambas banderas) */}
          {!isMobile && isDifferentCountry && patientDisplayTime && (
            <div
              data-testid="session-item-patient-time"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                opacity: 0.85,
                fontSize: '0.65rem',
                fontWeight: 500,
                overflow: 'hidden',
                minWidth: 0
              }}
            >
              <span style={{ flexShrink: 0 }}>{patientDisplayTime}</span>
              <span data-testid="session-item-patient-flag" aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
                <CountryFlag iso2={countryCode} size="0.7rem" />
              </span>
            </div>
          )}
        </>
      )}
    </div>
    )}
    {formacionData && (
      <FormacionBlockDialog data={formacionDetailsOpen ? formacionData : null} onClose={() => setFormacionDetailsOpen(false)} />
    )}
    </>
  );
};

export default SessionItem;


